import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronRight,
  TrendingUp,
  Target,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/layout/PageHeader';
import { contentApi } from '../lib/api';

const IMAGES = {
  cloud: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ai: "https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGFic3RyYWN0JTIwYmx1ZSUyMHRlY2h8ZW58MHx8fHwxNzczNzc1OTMyfDA&ixlib=rb-4.1.0&q=85",
  consulting: "https://images.unsplash.com/photo-1600528667634-9cf618cd6488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG1vZGVybiUyMG9mZmljZSUyMGdsYXNzJTIwd2FsbHN8ZW58MHx8fHwxNzczNzc1OTMxfDA&ixlib=rb-4.1.0&q=85"
};

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await contentApi.getCaseStudies();
        setCaseStudies(response.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching case studies:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  const industries = useMemo(() => 
    ['all', ...new Set(caseStudies.map(cs => cs.industry))], 
    [caseStudies]
  );
  
  const filteredCaseStudies = useMemo(() => 
    selectedIndustry === 'all' 
      ? caseStudies 
      : caseStudies.filter(cs => cs.industry === selectedIndustry),
    [caseStudies, selectedIndustry]
  );

  return (
    <div className="pt-20">
      <PageHeader 
        title="Case Studies"
        subtitle="Real results from real partnerships. See how we've helped enterprises transform with elite talent and innovative solutions."
        breadcrumbs={[{ label: "Case Studies" }]}
      />

      {/* Filter */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="filter-section">
        <div className="container-padding">
          <div className="flex flex-wrap gap-3">
            {industries.map((industry) => (
              <Button
                key={industry}
                variant={selectedIndustry === industry ? "default" : "outline"}
                onClick={() => setSelectedIndustry(industry)}
                className={selectedIndustry === industry ? "btn-primary" : "btn-outline"}
                data-testid={`filter-${industry}`}
              >
                {industry === 'all' ? 'All Industries' : industry}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="case-studies-grid">
        <div className="container-padding">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Loading case studies...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCaseStudies.map((caseStudy, index) => (
                <div 
                  key={caseStudy.id || index}
                  className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-300 group"
                  data-testid={`case-study-card-${index}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={caseStudy.image_url || IMAGES.consulting}
                      alt={caseStudy.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white/90 text-[#0A2540] hover:bg-white">
                        {caseStudy.industry}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#0A2540] mb-2 group-hover:text-[#2563EB] transition-colors">
                      {caseStudy.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {caseStudy.client}
                    </p>

                    {/* Challenge & Solution */}
                    <div className="space-y-4 mb-6">
                      <div className="flex items-start">
                        <Target className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Challenge</div>
                          <p className="text-sm text-slate-700 line-clamp-2">{caseStudy.challenge}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Lightbulb className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Solution</div>
                          <p className="text-sm text-slate-700 line-clamp-2">{caseStudy.solution}</p>
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="mb-6">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Results</span>
                      </div>
                      <ul className="space-y-1">
                        {caseStudy.results?.slice(0, 3).map((result, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start">
                            <ChevronRight className="w-4 h-4 text-[#2563EB] mr-1 flex-shrink-0 mt-0.5" />
                            {result}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {caseStudy.tags?.slice(0, 4).map((tag) => (
                        <span key={tag} className="text-xs bg-blue-50 text-[#2563EB] px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A2540]" data-testid="cta-section">
        <div className="container-padding text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Be Our Next Success Story?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how Global IT Associates can help transform your business with elite talent and innovative solutions.
          </p>
          <Link to="/contact">
            <Button className="btn-accent text-base" data-testid="cta-contact-btn">
              Start a Conversation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
